<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>HaioFlash IP History & Audit Logs</h2>
        <vs-button color="success" @click="loadEvents()">
          <feather-icon icon="RefreshCwIcon" class="mr-2"></feather-icon>
          Refresh
        </vs-button>
      </div>

      <!-- Quick Stats -->
      <div v-if="stats" class="stats-grid mb-6">
        <div class="stat-card">
          <div class="stat-icon" style="background: #7367F0;">
            <feather-icon icon="ActivityIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Total Events</p>
            <h3 class="stat-value">{{ stats.total_events || 0 }}</h3>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background: #28C76F;">
            <feather-icon icon="CheckCircleIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Allocated</p>
            <h3 class="stat-value">{{ stats.by_action && stats.by_action.allocated || 0 }}</h3>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background: #FF9F43;">
            <feather-icon icon="XCircleIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Released</p>
            <h3 class="stat-value">{{ stats.by_action && stats.by_action.released || 0 }}</h3>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background: #00CFE8;">
            <feather-icon icon="GlobeIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Unique IPs</p>
            <h3 class="stat-value">{{ stats.unique_ips || 0 }}</h3>
          </div>
        </div>
      </div>

      <!-- Search & Filters -->
      <div class="filters-section mb-4">
        <vs-row>
          <vs-col vs-w="3" vs-sm="6" vs-xs="12">
            <vs-input
              v-model="filters.ip_address"
              class="w-full"
              label-placeholder="IP Address (e.g., 1.1.1.1)"
              @keyup.enter="loadEvents()"
            >
              <template #icon>
                <feather-icon icon="GlobeIcon"></feather-icon>
              </template>
            </vs-input>
          </vs-col>
          <vs-col vs-w="2" vs-sm="6" vs-xs="12">
            <vs-input
              v-model="filters.user_id"
              class="w-full"
              label-placeholder="User ID"
              type="number"
              @keyup.enter="loadEvents()"
            >
              <template #icon>
                <feather-icon icon="UserIcon"></feather-icon>
              </template>
            </vs-input>
          </vs-col>
          <vs-col vs-w="2" vs-sm="6" vs-xs="12">
            <vs-input
              v-model="filters.vm_id"
              class="w-full"
              label-placeholder="VM ID"
              type="number"
              @keyup.enter="loadEvents()"
            >
              <template #icon>
                <feather-icon icon="ServerIcon"></feather-icon>
              </template>
            </vs-input>
          </vs-col>
          <vs-col vs-w="2" vs-sm="6" vs-xs="12">
            <vs-select
              v-model="filters.action"
              class="w-full"
              placeholder="Action Type"
              @change="loadEvents()"
            >
              <vs-select-item value="" text="All Actions"></vs-select-item>
              <vs-select-item value="allocated" text="Allocated"></vs-select-item>
              <vs-select-item value="released" text="Released"></vs-select-item>
              <vs-select-item value="reserved" text="Reserved"></vs-select-item>
              <vs-select-item value="disabled" text="Disabled"></vs-select-item>
            </vs-select>
          </vs-col>
          <vs-col vs-w="3" vs-sm="12" vs-xs="12" class="flex items-center">
            <vs-button color="primary" class="mr-2" @click="loadEvents()">
              <feather-icon icon="SearchIcon" class="mr-1"></feather-icon>
              Search
            </vs-button>
            <vs-button color="dark" type="border" @click="clearFilters()">
              Clear
            </vs-button>
          </vs-col>
        </vs-row>

        <!-- Date Range -->
        <vs-row class="mt-3">
          <vs-col vs-w="3" vs-sm="6" vs-xs="12">
            <vs-input
              v-model="filters.from"
              class="w-full"
              label-placeholder="From Date (YYYY-MM-DD)"
              type="date"
              @change="loadEvents()"
            ></vs-input>
          </vs-col>
          <vs-col vs-w="3" vs-sm="6" vs-xs="12">
            <vs-input
              v-model="filters.to"
              class="w-full"
              label-placeholder="To Date (YYYY-MM-DD)"
              type="date"
              @change="loadEvents()"
            ></vs-input>
          </vs-col>
        </vs-row>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions mb-4">
        <vs-button
          size="small"
          color="primary"
          type="border"
          @click="showIpLookup = true"
        >
          <feather-icon icon="SearchIcon" class="mr-1"></feather-icon>
          IP Lookup
        </vs-button>
        <vs-button
          size="small"
          color="success"
          type="border"
          class="ml-2"
          @click="showUserLookup = true"
        >
          <feather-icon icon="UserIcon" class="mr-1"></feather-icon>
          User IP History
        </vs-button>
        <vs-button
          size="small"
          color="warning"
          type="border"
          class="ml-2"
          @click="showVmLookup = true"
        >
          <feather-icon icon="ServerIcon" class="mr-1"></feather-icon>
          VM IP History
        </vs-button>
      </div>

      <!-- Events Table -->
      <div v-if="loading" class="text-center py-8">
        <vs-progress indeterminate color="primary"></vs-progress>
        <p class="mt-4 text-grey">Loading IP events...</p>
      </div>

      <div v-else-if="events && events.length > 0">
        <vs-table :data="events" stripe>
          <template slot="thead">
            <vs-th>Time</vs-th>
            <vs-th>Action</vs-th>
            <vs-th>IP Address</vs-th>
            <vs-th>VM</vs-th>
            <vs-th>User</vs-th>
            <vs-th>Previous VM</vs-th>
            <vs-th>New VM</vs-th>
            <vs-th>Reason</vs-th>
            <vs-th>Details</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <div class="font-mono text-sm">
                  {{ formatDate(tr.created_at) }}
                </div>
              </vs-td>
              <vs-td>
                <vs-chip :color="getActionColor(tr.action)" class="font-semibold">
                  {{ tr.action }}
                </vs-chip>
              </vs-td>
              <vs-td>
                <code class="ip-code" @click="quickSearchIp(getIpAddress(tr))">
                  {{ getIpAddress(tr) }}
                </code>
              </vs-td>
              <vs-td>
                <div v-if="tr.vm">
                  <a :href="`#/haioflash/vms?vm_id=${tr.vm.id}`" class="text-primary font-semibold">
                    #{{ tr.vm.id }}
                  </a>
                  <br>
                  <small class="text-grey">{{ tr.vm.name || 'N/A' }}</small>
                </div>
                <span v-else class="text-grey">-</span>
              </vs-td>
              <vs-td>
                <div v-if="tr.vm && tr.vm.user">
                  <a :href="`/user?id=${tr.vm.user.id}`" class="text-primary font-semibold">
                    {{ tr.vm.user.name }}
                  </a>
                  <br>
                  <small class="text-grey">ID: {{ tr.vm.user.id }}</small>
                </div>
                <span v-else class="text-grey">-</span>
              </vs-td>
              <vs-td>
                <code v-if="tr.previous_vm_id" class="text-sm">
                  VM #{{ tr.previous_vm_id }}
                </code>
                <span v-else class="text-grey">-</span>
              </vs-td>
              <vs-td>
                <code v-if="tr.new_vm_id" class="text-sm">
                  VM #{{ tr.new_vm_id }}
                </code>
                <span v-else class="text-grey">-</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ tr.reason || '-' }}</span>
              </vs-td>
              <vs-td>
                <vs-button
                  size="small"
                  color="primary"
                  type="flat"
                  @click="showEventDetails(tr)"
                >
                  <feather-icon icon="InfoIcon"></feather-icon>
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <!-- Pagination -->
        <div v-if="pagination" class="flex justify-between items-center mt-4">
          <div class="text-sm text-grey">
            Showing {{ events.length }} of {{ pagination.total }} events
          </div>
          <vs-pagination
            :total="pagination.last_page"
            v-model="currentPage"
            @change="loadEvents()"
          ></vs-pagination>
        </div>
      </div>

      <div v-else class="text-center py-8">
        <feather-icon icon="DatabaseIcon" svgClasses="text-grey" class="mb-4" style="width: 64px; height: 64px;"></feather-icon>
        <p class="text-grey">No IP events found. Try adjusting your filters.</p>
      </div>
    </vx-card>

    <!-- IP Lookup Dialog -->
    <vs-popup
      title="IP Address Lookup"
      :active.sync="showIpLookup"
      class="popup-lg"
    >
      <vs-input
        v-model="lookupIp"
        class="w-full mb-4"
        label-placeholder="Enter IP Address (e.g., 1.1.1.1)"
        @keyup.enter="doIpLookup()"
      ></vs-input>
      <vs-button color="primary" class="w-full" @click="doIpLookup()">
        <feather-icon icon="SearchIcon" class="mr-2"></feather-icon>
        Lookup IP History
      </vs-button>
    </vs-popup>

    <!-- User Lookup Dialog -->
    <vs-popup
      title="User IP History"
      :active.sync="showUserLookup"
      class="popup-lg"
    >
      <vs-input
        v-model="lookupUserId"
        class="w-full mb-4"
        label-placeholder="Enter User ID"
        type="number"
        @keyup.enter="doUserLookup()"
      ></vs-input>
      <vs-button color="success" class="w-full" @click="doUserLookup()">
        <feather-icon icon="UserIcon" class="mr-2"></feather-icon>
        Load User IPs
      </vs-button>
    </vs-popup>

    <!-- VM Lookup Dialog -->
    <vs-popup
      title="VM IP History"
      :active.sync="showVmLookup"
      class="popup-lg"
    >
      <vs-input
        v-model="lookupVmId"
        class="w-full mb-4"
        label-placeholder="Enter VM ID"
        type="number"
        @keyup.enter="doVmLookup()"
      ></vs-input>
      <vs-button color="warning" class="w-full" @click="doVmLookup()">
        <feather-icon icon="ServerIcon" class="mr-2"></feather-icon>
        Load VM IP History
      </vs-button>
    </vs-popup>

    <!-- Event Details Dialog -->
    <vs-popup
      :title="`Event #${selectedEvent && selectedEvent.id}`"
      :active.sync="showEventDetails"
      class="popup-lg"
    >
      <div v-if="selectedEvent">
        <div class="detail-row">
          <span class="detail-label">Action:</span>
          <vs-chip :color="getActionColor(selectedEvent.action)">
            {{ selectedEvent.action }}
          </vs-chip>
        </div>
        <div class="detail-row">
          <span class="detail-label">IP Address:</span>
          <code class="ip-code">{{ getIpAddress(selectedEvent) }}</code>
        </div>
        <div class="detail-row">
          <span class="detail-label">Timestamp:</span>
          <span>{{ formatDate(selectedEvent.created_at) }}</span>
        </div>
        <div class="detail-row" v-if="selectedEvent.vm">
          <span class="detail-label">VM:</span>
          <span>#{{ selectedEvent.vm.id }} - {{ selectedEvent.vm.name }}</span>
        </div>
        <div class="detail-row" v-if="selectedEvent.vm && selectedEvent.vm.user">
          <span class="detail-label">User:</span>
          <span>{{ selectedEvent.vm.user.name }} (ID: {{ selectedEvent.vm.user.id }})</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Reason:</span>
          <span>{{ selectedEvent.reason || 'N/A' }}</span>
        </div>
        <div class="detail-row" v-if="selectedEvent.metadata">
          <span class="detail-label">Metadata:</span>
          <pre class="metadata-box">{{ JSON.stringify(selectedEvent.metadata, null, 2) }}</pre>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'IpHistory',
  data () {
    return {
      events: [],
      stats: null,
      pagination: null,
      loading: false,
      currentPage: 1,
      filters: {
        ip_address: '',
        user_id: '',
        vm_id: '',
        action: '',
        from: '',
        to: ''
      },
      showIpLookup: false,
      showUserLookup: false,
      showVmLookup: false,
      showEventDetailsDialog: false,
      lookupIp: '',
      lookupUserId: '',
      lookupVmId: '',
      selectedEvent: null
    }
  },
  mounted () {
    this.loadEvents()
  },
  methods: {
    async loadEvents () {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          ...this.filters
        }
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const { data } = await this.$http.get('/admin/haioflash/ip-history', { params })
        
        if (data.success) {
          this.events = data.data.events || []
          this.stats = data.data.stats || null
          this.pagination = data.data.pagination || null
        }
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.response && error.response.data && error.response.data.message || 'Failed to load IP events',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    async doIpLookup () {
      if (!this.lookupIp) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter an IP address',
          color: 'warning'
        })
        return
      }

      this.showIpLookup = false
      this.filters.ip_address = this.lookupIp
      this.currentPage = 1
      await this.loadEvents()
    },

    async doUserLookup () {
      if (!this.lookupUserId) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter a user ID',
          color: 'warning'
        })
        return
      }

      this.showUserLookup = false
      this.filters.user_id = this.lookupUserId
      this.currentPage = 1
      await this.loadEvents()
    },

    async doVmLookup () {
      if (!this.lookupVmId) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter a VM ID',
          color: 'warning'
        })
        return
      }

      this.showVmLookup = false
      this.filters.vm_id = this.lookupVmId
      this.currentPage = 1
      await this.loadEvents()
    },

    quickSearchIp (ip) {
      if (ip) {
        this.filters.ip_address = ip
        this.currentPage = 1
        this.loadEvents()
      }
    },

    clearFilters () {
      this.filters = {
        ip_address: '',
        user_id: '',
        vm_id: '',
        action: '',
        from: '',
        to: ''
      }
      this.currentPage = 1
      this.loadEvents()
    },

    showEventDetails (event) {
      this.selectedEvent = event
      this.showEventDetailsDialog = true
    },

    getActionColor (action) {
      const colors = {
        allocated: 'success',
        released: 'warning',
        reserved: 'primary',
        disabled: 'danger'
      }
      return colors[action] || 'dark'
    },

    formatDate (date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getIpAddress (event) {
      if (!event) return 'N/A'
      if (event.ip_address) {
        if (typeof event.ip_address === 'string') {
          return event.ip_address
        }
        if (event.ip_address.ip_address) {
          return event.ip_address.ip_address
        }
      }
      return 'N/A'
    }
  }
}
</script>

<style scoped lang="scss">
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  margin-right: 1rem;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c2c2c;
  margin: 0;
}

.filters-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ip-code {
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e0e0e0;
  }
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-weight: 600;
  width: 150px;
  color: #6c757d;
}

.metadata-box {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  overflow-x: auto;
  max-width: 100%;
}

.popup-lg {
  .vs-popup {
    width: 600px !important;
    max-width: 90vw;
  }
}
</style>
